import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ClimateRiskType, BlockItemType } from "../../types";
import { blockItems } from "../../constants";
import { getLocalStorageItem } from "../../app/functions";

// Import all search blocks
import InfoResultBlock from "./InfoResultBlock";
import BusinessSearchResultBlock from "./BusinessSearchResultBlock";
import BusinessAddressAtPlaceResultBlock from "./BusinessAddressAtPlaceResultBlock";
import CombinedImageSearchResultBlock from "./CombinedImageSearchResultBlock";
import WeatherSearchResultBlock from "./WeatherSearchResultBlock";
// import WeatheObservationsResultBlock from "./WeatherObservationsResultBlock";
// import ClimateRiskResultBlock from "./ClimateRiskResultBlock";
import CatnatRiskListBlock from "./CatnatRiskListBlock";
import PerilRiskListBlock from "./PerilRiskListBlock";
import SiteSevesoListBlock from "./SiteSevesoListBlock";
import EventListBlock from "./EventListBlock";
import CoproTabContent, { COPRO_TAB_INDEX } from "./CoproTabContent";
import { CoproPortfolioOpenRequest } from "./coproPortfolio";

interface DataTabsProps {
  address: any;
  selectedRisk: ClimateRiskType | null;
  climateRisk: any;
  userInfo: any;
  onTabChange?: (tabIndex: number) => void;
  selectedTab?: number;
  onHistoricMonumentClick?: () => void;
  onZoneSurveilleeClick?: () => void;
  onFireStationClick?: () => void;
  onGasStationClick?: () => void;
  arretePerils: any;
  selectedCoproIndex?: number;
  onSelectedCoproIndexChange?: (index: number) => void;
  onViewPortfolio?: (request: CoproPortfolioOpenRequest) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, style, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-tabpanel-${index}`}
      aria-labelledby={`data-tab-${index}`}
      style={{ height: "calc(100% - 48px)", overflow: "auto", ...style }}
      {...other}
    >
      {value === index && <Box sx={{ p: 2, height: "100%" }}>{children}</Box>}
    </div>
  );
}

export default function DataTabs({ address, selectedRisk, climateRisk, userInfo, onTabChange, selectedTab, onHistoricMonumentClick, onZoneSurveilleeClick, onFireStationClick, onGasStationClick, arretePerils, selectedCoproIndex = 0, onSelectedCoproIndexChange, onViewPortfolio }: DataTabsProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [blockItemList, setBlockItemList] = useState<BlockItemType[]>([]);
  const [forceSync, setForceSync] = useState(0);
  const { t } = useTranslation();
  const [tabs, setTabs] = useState<any[]>([]);

  // Initialize block items based on user permissions
  useEffect(() => {
    const storedBlockItemList = getLocalStorageItem("block-result-list", []);
    
    const initialBlockItemList = storedBlockItemList.filter((item: any) => item.id !== "streetview_image_search" && item.id !== "satellite_image_search" && item.id !== "climate_risk");
    let hasStreetView = false;
    let hasSatellite = false;
    
    // Filter out climate risk block
    for (let item of blockItems.filter((block) => block.id !== "climate_risk")) {
      const userHasAccessRight = !item.accessRight || (userInfo.value && userInfo.value.groups && userInfo.value.groups.includes(item.accessRight));
      const itemAlreadyExists = initialBlockItemList.filter(function(initialItem: any) {
        return item.id === initialItem.id;
      }).length > 0;

      // Check if we have streetview or satellite access
      if (item.id === "streetview_image_search" && userHasAccessRight) {
        hasStreetView = true;
      }
      if (item.id === "satellite_image_search" && userHasAccessRight) {
        hasSatellite = true;
      }

      // Add the item if it's not streetview or satellite (we'll handle them separately)
      if (userHasAccessRight && !itemAlreadyExists && item.id !== "streetview_image_search" && item.id !== "satellite_image_search") {
        initialBlockItemList.push(item);
      }
    }

    // Add combined image block if we have access to either streetview or satellite
    if ((hasStreetView || hasSatellite) && !initialBlockItemList.find((item: any) => item.id === "combined_images")) {
      initialBlockItemList.push({
        title: "blockTitle.images",
        id: "images",
        iconType: "paperPlane", // Using paperPlane as default icon
      });
    }

    const iAddr = initialBlockItemList.findIndex((x: any) => x.id === "business_address_at_place");
    const iProx = initialBlockItemList.findIndex((x: any) => x.id === "business_search");
    if (iAddr >= 0 && iProx >= 0 && iAddr > iProx) {
      const [atPlaceBlock] = initialBlockItemList.splice(iAddr, 1);
      initialBlockItemList.splice(iProx, 0, atPlaceBlock);
    }

    setBlockItemList(initialBlockItemList);
  }, [userInfo]);

  // Map hash to tab index (tab 0 = first block, tab 1 = Copropriété, tab 2+ = next blocks)
  const hashToTabIndex = (hash: string): number | null => {
    if (hash === 'copro') return COPRO_TAB_INDEX;
    const blockIndex = blockItemList.findIndex(block => block.id === hash);
    if (blockIndex === -1) return null;
    return blockIndex === 0 ? 0 : blockIndex + 1;
  };

  // Handle URL hash navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && blockItemList.length > 0) {
        const tabIndex = hashToTabIndex(hash);
        if (tabIndex !== null) {
          setValue(tabIndex);
          if (onTabChange) onTabChange(tabIndex);
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [blockItemList, onTabChange]);

  // Additional effect to handle hash changes when blockItemList becomes available
  useEffect(() => {
    if (blockItemList.length > 0) {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const tabIndex = hashToTabIndex(hash);
        if (tabIndex !== null) {
          setValue(tabIndex);
          if (onTabChange) onTabChange(tabIndex);
        }
      }
    }
  }, [blockItemList, onTabChange]);

  // Force sync when hash changes externally (browser navigation)
  useEffect(() => {
    const handleHashChange = () => {
      setForceSync(prev => prev + 1);
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Sync with hash when forceSync changes
  useEffect(() => {
    if (blockItemList.length > 0) {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        const tabIndex = hashToTabIndex(hash);
        if (tabIndex !== null && tabIndex !== value) {
          setValue(tabIndex);
          if (onTabChange) onTabChange(tabIndex);
        }
      }
    }
  }, [forceSync, blockItemList, value, onTabChange]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (onTabChange) onTabChange(newValue);
    const newHash = newValue === COPRO_TAB_INDEX ? 'copro' : (newValue === 0 ? blockItemList[0]?.id : blockItemList[newValue - 1]?.id);
    // replace : le hash d'onglet reste sur l'adresse courante. window.location.hash
    // poussait une entrée hors de React Router et faisait sauter une adresse au Retour.
    if (newHash && window.location.hash !== `#${newHash}`) {
      navigate(
        { search: window.location.search, hash: `#${newHash}` },
        { replace: true }
      );
    }
  };

  // Sync with external selectedTab prop
  useEffect(() => {
    if (selectedTab !== undefined && selectedTab !== value) {
      setValue(selectedTab);
    }
  }, [selectedTab, value]);

  // Function to render search blocks
  const renderSearchBlocks = () => {
    return blockItemList.map((item: BlockItemType, index: number) => {
      const searchBlocks: Record<string, JSX.Element> = {
        map_search: (
          <InfoResultBlock 
            key={`search_block_${index}`} 
            {...item} 
            onHistoricMonumentClick={onHistoricMonumentClick} 
            onZoneSurveilleeClick={onZoneSurveilleeClick}
            onFireStationClick={onFireStationClick}
            onGasStationClick={onGasStationClick}
          />
        ),
        business_search: (
          <BusinessSearchResultBlock key={`search_block_${index}`} {...item} />
        ),
        business_address_at_place: (
          <BusinessAddressAtPlaceResultBlock key={`search_block_${index}`} {...item} />
        ),
        images: (
          <CombinedImageSearchResultBlock key={`combined_image_block_${index}`} {...item} />
        ),
        weather_search: (
          <WeatherSearchResultBlock key={`search_block_${index}`} {...item} />
        ),
        // weather_observations: (
        //   <WeatheObservationsResultBlock key={`search_block_${index}`} {...item} />
        // ),
        // climate_risk: (
        //   <ClimateRiskResultBlock key={`climate_risk_block_${index}`} {...item} />
        // ),
        catnat_risk: (
          <CatnatRiskListBlock key={`catnat_block_${index}`} {...item} />
        ),
        peril_risk: (
          <PerilRiskListBlock key={`peril_block_${index}`} {...item} />
        ),
        event_risk: (
          <EventListBlock key={`event_block_${index}`} {...item}/>
        ),
        site_seveso: (
          <SiteSevesoListBlock key={`seveso_block_${index}`} {...item} />
        ),
      };
      return searchBlocks[item.id];
    });
  };

  // Create tabs: [block0, Copropriété, block1, block2, ...]
  const createTabs = () => {
    const tabs: any[] = [];
    const coproCount = (address?.placeCopro?.copros?.length ?? 0) + (address?.placeCopro?.nearbyCopro ? 1 : 0);
    const coproLabel = coproCount > 0
      ? <span>Copropriété (<span style={{ color: "red", fontWeight: "bold" }}>{coproCount}</span>)</span>
      : <span>Copropriété</span>;

    blockItemList.forEach((block, index) => {
      if (index === 0) {
        tabs.push({ label: <span>{t(block.title)}</span>, id: block.id, blockIndex: 0 });
        tabs.push({ label: coproLabel, id: 'copro', blockIndex: -1 });
      } else {
        let label: string | JSX.Element = <span>{t(block.title)}</span>;
        if (block.id === "peril_risk" && arretePerils.value?.features?.length > 0) {
          label = <span>{label} (<span style={{ color: "red", fontWeight: "bold" }}>{arretePerils.value.features.length}</span>)</span>;
        }
        if (block.id === "business_address_at_place") {
          const ba = address?.businessAddressAtPlace;
          const n =
            ba?.properties?.totalCount ?? ba?.features?.length ?? 0;
          label = (
            <span>
              {label} (<span style={{ color: "red", fontWeight: "bold" }}>{n}</span>)
            </span>
          );
        }
        tabs.push({ label, id: block.id, blockIndex: index });
      }
    });
    return tabs;
  };

  useEffect(() => {
    setTabs(createTabs());
  }, [blockItemList, arretePerils, address?.placeCopro, address?.businessAddressAtPlace, t]);

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs 
          value={value} 
          onChange={handleChange} 
          aria-label="data tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Informations Tab */}
      {/* <TabPanel value={value} index={0}>
        {address ? (
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Adresse" 
                secondary={address.address} 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Coordonnées" 
                secondary={`${address.latitude}, ${address.longitude}`} 
              />
            </ListItem>
            {selectedRisk && (
              <ListItem>
                <ListItemText 
                  primary="Risque sélectionné" 
                  secondary={t(selectedRisk.label)} 
                />
              </ListItem>
            )}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Aucune adresse sélectionnée
          </Typography>
        )}
      </TabPanel> */}

      {/* Dynamic Tabs: index 0 = block0, 1 = Copropriété, 2+ = block1, block2, ... */}
      {tabs.map((tab, tabIndex) => (
        <TabPanel key={tab.id} value={value} index={tabIndex} style={{ flex: 1, overflow: "hidden" }}>
          {tabIndex === COPRO_TAB_INDEX ? (
            <CoproTabContent
              placeCopro={address?.placeCopro}
              selectedCoproIndex={selectedCoproIndex}
              onSelectedCoproIndexChange={onSelectedCoproIndexChange ?? (() => {})}
              onViewPortfolio={onViewPortfolio}
            />
          ) : (
            tab.blockIndex >= 0 ? renderSearchBlocks()[tab.blockIndex] : null
          )}
        </TabPanel>
      ))}
    </Box>
  );
} 
